import { useEffect, useRef, useState } from 'react'
import useDelayedInterval from './useDelayedInterval'

type ReturnProps = {
    isPending: boolean
    hasTimedOut: boolean
    start: () => void
}

/**
 * Await an entity that a third party generates asynchronously: the generate call answers long
 * before the result exists, so poll the list until an entity turns up that we did not know yet.
 *
 * The hook stays inert until `start()` is called, which is meant to happen right after the generate
 * request succeeds: it records the ids that exist at that moment and starts polling. Polling stops
 * as soon as an id appears that was not in that set, or after `maxAttempts` polls without one.
 *
 * @param entities the currently known entities, as loaded by useEntity; polling watches this list.
 * @param reloadEntities reloads that list in place; called on every poll. Pass useEntity's reload
 *     (its fourth tuple element) rather than its reset, so the list keeps showing its current data
 *     while refreshing.
 * @param onGenerated called once with the entity that was generated, right before polling stops.
 * @param interval milliseconds between two polls.
 * @param maxAttempts how many polls to do before giving up and reporting `hasTimedOut`.
 * @returns `isPending` while polling — drive a message with it and disable the generate action —
 *     `hasTimedOut` when it gave up, and `start` to begin awaiting the next generated entity.
 */
export default function useAwaitGeneratedEntity<T extends { id: string }>(
    entities: T[] | undefined,
    reloadEntities: () => void,
    onGenerated: (entity: T) => void,
    interval = 5_000,
    maxAttempts = 60,
): ReturnProps {
    const [pending, setPending] = useState<{ knownIds: string[]; attempts: number }>()
    const [hasTimedOut, setHasTimedOut] = useState<boolean>(false)

    // Both are read from callbacks that outlive the render they were created in.
    const entitiesReference = useRef(entities)
    entitiesReference.current = entities
    const onGeneratedReference = useRef(onGenerated)
    onGeneratedReference.current = onGenerated

    const { setRunning } = useDelayedInterval(interval, () => {
        setPending(previous => previous && { ...previous, attempts: previous.attempts + 1 })
        reloadEntities()
    })

    useEffect(() => {
        if (!pending) {
            return
        }

        const generated = (entities || []).find(entity => !pending.knownIds.includes(entity.id))
        if (generated) {
            setPending(undefined)
            setRunning(false)
            onGeneratedReference.current(generated)
        } else if (pending.attempts >= maxAttempts) {
            // Give up rather than poll forever; the entity may still turn up on a later visit.
            setPending(undefined)
            setRunning(false)
            setHasTimedOut(true)
        }
    }, [entities, pending])

    return {
        isPending: !!pending,
        hasTimedOut,
        start: () => {
            setHasTimedOut(false)
            setPending({ knownIds: (entitiesReference.current || []).map(entity => entity.id), attempts: 0 })
            setRunning(true)
        },
    }
}
